import React, {useEffect, useRef, useState} from 'react';
import UnsavedChangesModal from '../common/aur-unsaved-changes-modal/AurUnsavedChangesModal';
import AurPageHeader from '../common/aur-page-header/AurPageHeader';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {setShowModal} from '../../redux/slices/unSaveChangesModalSlice';
import {RootState} from '../../redux/store';
import BulkUserResultScreenTable from './BulKUserResultScreenTable';
import BulkUerEditState from './BulkUserEditState';
import AurButton from '../common/aur-button/AurButton';
import {Typography} from '@mui/material';
import AurSvgIcon from '../common/aur-svg-icon/AurSvgIcon';
import BulkUserUploadState from './BulkUserUploadState';
import {bulkDownloadErrorTemplate} from '../../api/UserManagementService';
import {setSelectedClient} from '../../redux/slices/bulkUserFileNameSlice';
import {setValue} from '../../redux/slices/bulkUserEditSlice';
import {useAnalytics} from '../../hooks/useAnalytics';
import {
  GA_EVENT_NAMES,
  GA_CLICK_TYPES,
  GA_REGIONS,
  GA_FILE_EXTENTIONS,
  GA_DATA_MODE_AUTO,
  GA_ERROR_CODES,
} from '../../constants/analyticsConstants';
import {UrlPathEnums} from '../../utils/enumUtils';

const BulkUserUploadResultScreen: React.FC = () => {
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const showModal = useSelector(
    (state: RootState) => state.unsavedChangesModal.showModal
  );
  const userImportTempId = useSelector(
    (state: RootState) => state.bulkUserFile.fileUploadBatchId
  );
  const summaryState = useSelector(
    (state: RootState) => state.bulkUserEdit.summary
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const totalErrorCount = summaryState?.totalErrors ?? 0;
  const totalUsers = summaryState?.totalUsers ?? 0;

  const {showDrawer} = useSelector((state: RootState) => state.bulkUserEdit);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const showUnsavedChangesModal = () => {
    dispatch(setShowModal(true));
  };

  useEffect(() => {
    localStorage.setItem('currentPath', globalThis.location.pathname);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      dispatch(setValue(0));

      if (!isRefreshing) {
        event.preventDefault();
        event.returnValue = '';

        return false;
      }
    };

    const handlePopState = () => {
      // Handle back button (popstate event)
      dispatch(setShowModal(true)); // Show custom modal
      globalThis.history.pushState(null, '', globalThis.location.pathname); // Prevent back navigation
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Detect refresh events (Ctrl+R or F5)
      if ((event.ctrlKey && event.key === 'r') || event.key === 'F5') {
        setIsRefreshing(true);
      }
    };

    // Add initial state to prevent back button navigation
    globalThis.history.pushState(null, '', globalThis.location.pathname);

    // Add event listeners for beforeunload and popstate
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    globalThis.addEventListener('popstate', handlePopState);
    globalThis.addEventListener('keydown', handleKeyDown);
    // Cleanup event listeners when component unmounts
    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.removeEventListener('popstate', handlePopState);
      globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch, isRefreshing]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.PAGE_VIEW,
        page: GA_DATA_MODE_AUTO,
        user: GA_DATA_MODE_AUTO,
        ...(!!totalErrorCount && {
          error: {
            type: t('gaTracking.users.groupTeamMemberFileError'),
            text: t('gaTracking.users.totalUserTotalError', {
              totalUsers: totalUsers,
              totalErrorCount: totalErrorCount,
            }),
            code: GA_ERROR_CODES.BAD_REQUEST,
          },
        }),
      });
    }, 1500);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [totalUsers, totalErrorCount]);

  const startOverHandler = () => {
    showUnsavedChangesModal();
    dispatch(setSelectedClient(''));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.startOver'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.users.startOverAction'),
      },
    });
  };

  const addNewGroupHandler = () => {
    showUnsavedChangesModal();
  };

  const onDownloadErrorFile = async () => {
    // Need to remove static clientId once we clientId from clientservices
    try {
      const response = await bulkDownloadErrorTemplate(userImportTempId);
      // Create a Blob from the response data and Set the default file name
      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'errorTemplate.xlsx';
      document.body.appendChild(a);
      a.click();
      globalThis.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
        file_extension: GA_FILE_EXTENTIONS.XLSX,
        click: {
          name: t('gaTracking.users.errorFile'),
          type: GA_CLICK_TYPES.LINK,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.users.errorFileToWorkOffline'),
        },
      });
    } catch (error) {
      console.error('Error downloading the template:', error);
    }
  };
  const selectedClientName = useSelector(
    (state: RootState) => state.bulkUserFile.selectedClient
  );

  return (
    <>
      <header>
        <div className="custom-header">
          <AurPageHeader
            tabIndex={0}
            ariaLabel={t('common.bulkUploadModule.heading')}
            linkLabel={t('common.bulkUploadModule.addTeamMembers')}
            heading={t('common.bulkUploadModule.heading')}
            isSecondaryButton={true}
            onLinkCLick={addNewGroupHandler}
            onButtonClick={startOverHandler}
            selectedClientName={selectedClientName}
            path={UrlPathEnums.UsersBulkUploadAdd}
          />
        </div>
        <BulkUserUploadState />
        {totalErrorCount > 0 && (
          <div className="download-csv-container">
            <Typography className="typo-text body3_size mist-color-1400">
              {t('common.bulkUploadModule.downloadErrorsText')}{' '}
            </Typography>
            <AurButton
              type={'text'}
              label={t('common.bulkUploadModule.downloadCsvText')}
              startIcon={<AurSvgIcon src="images/download_icon.svg" />}
              onClick={onDownloadErrorFile}
              size="Small"
              ariaLabel={t('common.bulkUploadModule.downloadErrors')}
              classNames="download-csv-button"
              title={t('common.bulkUploadModule.downloadFileHint')}
            />
          </div>
        )}
        <BulkUserResultScreenTable />
      </header>
      {showModal ? <UnsavedChangesModal /> : null}
      {showDrawer ? <BulkUerEditState /> : null}
    </>
  );
};

export default BulkUserUploadResultScreen;
